import React, { useEffect, useState } from "react";
import { Button, Col, Container, Row, Spinner } from "react-bootstrap";
import { fetchNoteTypes } from "../../../services/httpClient";
import useApp from "../../../utils/useApp";
import SaveChangesModal from "../../ui/SaveChangesModal";
import { unsavedChanges } from "../../../App";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBullhorn, faEdit } from "@fortawesome/free-solid-svg-icons";
import { NoteType } from "../../../types/notetype";
import NoteTypeCard from "./NoteTypeCard";
import CreateNoteTypeCard from "./CreateNoteTypeCard";
import FieldChoices from "./FieldChoices";
import PageTitle from "../../ui/PageTitle";

interface CustomState {
  setOrgToSave: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const OrgCustomNoteTypes = ({ setOrgToSave, setShowToast, setToastText, setToastBody }: CustomState) => {
  const { auth, showMenu }: any = useApp();
  const [errMsg, setErrMsg] = useState("");
  const [showCreateType, setShowCreateType] = useState<boolean>(false);
  const [infractionType, setInfractionType] = useState<NoteType>();
  const [infractionTypeFieldId, setInfractionTypeFieldId] = useState<string>("");
  const [noteTypes, setNoteTypes] = useState<NoteType[]>([]);
  const [defaultTypes, setDefaultTypes] = useState<NoteType[]>([]);
  const [draftNoteTypes, setDraftNoteTypes] = useState<NoteType[]>([]);
  const [publishedNoteTypes, setPublishedNoteTypes] = useState<NoteType[]>([]);
  const [archivedNoteTypes, setArchivedNoteTypes] = useState<NoteType[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showPublished, setShowPublished] = useState<boolean>(false);
  const [showDefault, setShowDefault] = useState<boolean>(false);
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [showInfractionTypes, setShowInfractionTypes] = useState<boolean>(false);
  const [forceShowAddChoice, setForceShowAddChoice] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getNoteTypes(false);
  }, [auth?.profile]);

  const getNoteTypes = (showPub: boolean) => {
    setIsLoading(true);

    fetchNoteTypes()
      .then((res: any) => {
        var types = res[0].data;
        const rDefaultTypes = types.filter((t: any) => {
          if (t?.default_is_published || t?.default_is_published === 0) {
            t.is_published = t.default_is_published;
          }
          return t?.organization_id === null && t?.type === "user";
        });
        setDefaultTypes(rDefaultTypes);
        const infraction = types.find((t: any) => t.key === "infraction" && t.type === "user");
        setInfractionType(infraction);
        const fieldId = infraction?.fields.find((f: any) => f.key === "type");
        setInfractionTypeFieldId(fieldId?.id);
        types = types.filter((t: any) => {
          return t?.organization_id !== null;
        });

        const archivedTypes = types.filter((t: any) => t.is_archived === 1);
        setArchivedNoteTypes(archivedTypes);
        const unArchivedTypes = types.filter((t: any) => t.is_archived !== 1);
        setNoteTypes(unArchivedTypes);

        if (showPub) {
          setPublishedNoteTypes(unArchivedTypes.filter((t: any) => t.is_published === 1));
        } else {
          setDraftNoteTypes(unArchivedTypes.filter((t: any) => t.is_published !== 1));
        }
        setShowPublished(showPub);
        setIsLoading(false);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        setIsLoading(false);
      });
  };

  const onClickCreateType = () => {
    setErrMsg("");
    setShowCreateType(true);
    setShowArchived(false);
  };

  const onClickCreateTypeCancel = () => {
    setErrMsg("");
    if (unsavedChanges.value) {
      setShowModal(true);
    } else {
      setShowCreateType(false);
      unsavedChanges.value = false;
      setOrgToSave(false);
      getNoteTypes(false);
    }
  };

  const onLeaveCreateNoteType = () => {
    setErrMsg("");
    setShowCreateType(false);
    unsavedChanges.value = false;
    setOrgToSave(false);
  };

  const onViewPublished = (publish: boolean) => {
    setShowPublished(publish);
    var filtered;
    if (publish) {
      filtered = noteTypes.filter((f: any) => f.is_published === 1);
      setPublishedNoteTypes(filtered);
    } else {
      filtered = noteTypes.filter((f: any) => f.is_published !== 1);
      setDraftNoteTypes(filtered);
    }
  };

  const onViewDefault = () => {
    setShowDefault(true);
  };

  const onClickBack = () => {
    setShowInfractionTypes(false);
    setShowArchived(false);
    setShowDefault(false);
    setErrMsg("");
  };

  return (
    <>
      {isLoading ? (
        <Container className={showMenu ? "lg div-main-menu" : "lg div-main"}>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : showDefault ? (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <PageTitle title="Default Note Types" type="section" backButtonText="Back to Custom Note Types" onBackClick={onClickBack} />
          <Row className="mt-3">
            <Col xs={12}>
              {defaultTypes?.length === 0 ? (
                <p className="mt-2">No Default Note Types</p>
              ) : (
                defaultTypes.map((t: NoteType, index: number) => (
                  <NoteTypeCard
                    key={index}
                    type={t}
                    setErrMessage={setErrMsg}
                    getNotes={getNoteTypes}
                    setShowArchived={setShowArchived}
                    setShowDefault={setShowDefault}
                    setOrgToSave={setOrgToSave}
                    setShowToast={setShowToast}
                    setToastText={setToastText}
                    setToastBody={setToastBody}
                    isDefault={showDefault}
                  />
                ))
              )}
            </Col>
          </Row>
        </>
      ) : showInfractionTypes ? (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <PageTitle
            title="Infraction Type Choices"
            type="section"
            buttonTitle="Add Choice"
            onButtonClick={() => setForceShowAddChoice(true)}
            buttonDisabled={forceShowAddChoice}
            backButtonText="Back to Custom Note Types"
            onBackClick={onClickBack}
          />
          <Row className="mt-3">
            <Col xs={12}>
              <FieldChoices
                noteTypeId={infractionType?.id}
                fieldId={infractionTypeFieldId}
                getNotes={getNoteTypes}
                archived={false}
                published={false}
                forceShowAddChoice={forceShowAddChoice}
                setForceShowAddChoice={setForceShowAddChoice}
                hideAddChoiceButton={showInfractionTypes}
                setErrMessage={setErrMsg}
                setOrgToSave={setOrgToSave}
                setShowToast={setShowToast}
                setToastText={setToastText}
                setToastBody={setToastBody}
              />
            </Col>
          </Row>
        </>
      ) : showArchived ? (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <PageTitle title="Archived Note Types" type="section" backButtonText="Back to Custom Note Types" onBackClick={onClickBack} />
          <Row className="mt-3">
            <Col xs={12}>
              {archivedNoteTypes?.length === 0 ? (
                <p className="mt-2">No Archived Custom Note Types</p>
              ) : (
                archivedNoteTypes.map((t: NoteType, index: number) => (
                  <NoteTypeCard
                    key={index}
                    type={t}
                    setErrMessage={setErrMsg}
                    getNotes={getNoteTypes}
                    setShowArchived={setShowArchived}
                    setShowDefault={setShowDefault}
                    setOrgToSave={setOrgToSave}
                    setShowToast={setShowToast}
                    setToastText={setToastText}
                    setToastBody={setToastBody}
                    isDefault={false}
                  />
                ))
              )}
            </Col>
          </Row>
        </>
      ) : (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          {showCreateType ? (
            <PageTitle title="Create Note Type" type="section" backButtonText="Back to Custom Note Types" onBackClick={onClickCreateTypeCancel} />
          ) : (
            <Row className="mb-4">
              <Col className="col-3">
                <h4 className="app-header-text">Custom Note Types</h4>
              </Col>
              <Col className="text-end col-9">
                <Button className="app-secondary-color me-3 button" onClick={() => setShowInfractionTypes(true)}>
                  Infraction Types
                </Button>
                <Button className="app-secondary-color me-3 button" onClick={() => onViewDefault()}>
                  Default
                </Button>
                <Button className="app-secondary-color me-3 button" onClick={() => setShowArchived(true)}>
                  Archived
                </Button>
                <Button className="app-primary-color button" onClick={onClickCreateType}>
                  Create Type
                </Button>
              </Col>
            </Row>
          )}
          {showCreateType ? (
            <CreateNoteTypeCard
              getNoteTypes={getNoteTypes}
              setOrgToSave={setOrgToSave}
              setShowToast={setShowToast}
              setToastText={setToastText}
              setToastBody={setToastBody}
              setShowCreateType={setShowCreateType}
              setIsLoading={setIsLoading}
              setErrMsg={setErrMsg}
            />
          ) : (
            <>
              <Row className="mb-4">
                <Col></Col>
                <Col className="col-3 d-grid">
                  <Button
                    className={!showPublished ? "app-primary-color button" : "app-primary-color-inverse button"}
                    onClick={(e) => onViewPublished(false)}
                  >
                    <FontAwesomeIcon icon={faEdit} className={"me-2"} />
                    {"Drafts"}
                  </Button>
                </Col>
                <Col className="col-3 d-grid">
                  <Button
                    className={showPublished ? "app-primary-color button" : "app-primary-color-inverse button"}
                    onClick={() => onViewPublished(true)}
                  >
                    <FontAwesomeIcon icon={faBullhorn} className={"me-2"} />
                    {"Published"}
                  </Button>
                </Col>
                <Col></Col>
              </Row>
              <Row className="mt-3">
                <Col xs={12}>
                  {showPublished && publishedNoteTypes?.length === 0 ? (
                    <p className="mt-2">No Published Custom Note Types</p>
                  ) : !showPublished && draftNoteTypes?.length === 0 ? (
                    <p className="mt-2">No Draft Custom Note Types</p>
                  ) : showPublished ? (
                    publishedNoteTypes.map((t: NoteType) => (
                      <NoteTypeCard
                        key={t.id}
                        type={t}
                        setErrMessage={setErrMsg}
                        getNotes={getNoteTypes}
                        setShowArchived={setShowArchived}
                        setShowDefault={setShowDefault}
                        setOrgToSave={setOrgToSave}
                        setShowToast={setShowToast}
                        setToastText={setToastText}
                        setToastBody={setToastBody}
                        isDefault={false}
                      />
                    ))
                  ) : (
                    draftNoteTypes.map((t: NoteType) => (
                      <NoteTypeCard
                        key={t.id}
                        type={t}
                        setErrMessage={setErrMsg}
                        getNotes={getNoteTypes}
                        setShowArchived={setShowArchived}
                        setShowDefault={setShowDefault}
                        setOrgToSave={setOrgToSave}
                        setShowToast={setShowToast}
                        setToastText={setToastText}
                        setToastBody={setToastBody}
                        isDefault={false}
                      />
                    ))
                  )}
                </Col>
              </Row>
            </>
          )}
          <SaveChangesModal showModal={showModal} setShowModal={setShowModal} onLeave={onLeaveCreateNoteType} />
        </>
      )}
    </>
  );
};

export default React.memo(OrgCustomNoteTypes);
